import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  TEditorAtOptions,
  TEditorHashOptions,
  useEditorAtOptions,
  useEditorHashAgentOptions,
  useEditorHashContactOptions,
  useEditorHashConversationOptions,
  useEditorHashFieldOptions,
} from "@Hooks";
import { EditorFroala } from "@Components/editor-froala";
import { IFormElementHtml } from "@Modules/atlas-form-core";

interface ITextMessageEditorProps {
  item: IFormElementHtml;
  organizationId?: number;
  defaultValue: string;
  onChange: (value: string) => void;
  onBlur: (value: string) => void;
  disableRichText?: boolean;
}

export const TextMessageEditor: React.FC<ITextMessageEditorProps> = ({
  item,
  organizationId,
  defaultValue,
  onChange,
  onBlur,
  disableRichText,
}) => {
  const { t } = useTranslation();
  const atUserOptions = useEditorAtOptions();
  const hashContactOptions = useEditorHashContactOptions(t);
  const hashFieldOptions = useEditorHashFieldOptions(t, organizationId);
  const hashAgentOptions = useEditorHashAgentOptions(t);
  const hashConversationOptions = useEditorHashConversationOptions(t);
  const [processedAtOptions, setProcessedAtOptions] = useState<TEditorAtOptions>([]);
  const [processedHashOptions, setProcessedHashOptions] = useState<TEditorHashOptions>([]);

  // Process atOptions
  useEffect(() => {
    if (!atUserOptions.length) return;

    const processed = (item.atOptions || [])
      .map((key) => {
        const optionMap = {
          user: atUserOptions,
        };
        return optionMap[key] || [];
      })
      .flat();

    setProcessedAtOptions(processed);
  }, [item.atOptions, atUserOptions]);

  // Process hashOptions
  useEffect(() => {
    const processed = (item.hashOptions || [])
      .map((key) => {
        const optionMap = {
          contact: hashContactOptions,
          agent: hashAgentOptions,
          conversation: hashConversationOptions,
          field: hashFieldOptions,
        };
        return optionMap[key] || [];
      })
      .flat();

    setProcessedHashOptions(processed);
  }, [
    item.hashOptions,
    hashContactOptions,
    hashAgentOptions,
    hashConversationOptions,
    hashFieldOptions,
  ]);

  // Don't render until options are ready
  if (!processedAtOptions || !processedHashOptions) return null;

  return (
    <EditorFroala
      disableRichText={disableRichText}
      placeholder={item.placeholder}
      className="forms-input"
      atOptions={processedAtOptions}
      hashOptions={processedHashOptions}
      onChangeTrigger={onChange}
      onBlur={onBlur}
      defaultValue={defaultValue}
      canned_answers={item.canned_answers}
      style={item.element_style}
      file_upload={item.file_upload}
      disabled={item.disabled}
      tableImport={item.tableImport}
      maxLength={640}
      editorToggle={undefined}
    />
  );
};
